export interface LandingNavItem {
  label: string;
  href: `#${string}`;
}

export interface ComparisonSectionData {
  id?: `#${string}`;
  title: string;
  leftTitle: string;
  leftSubtitle: string;
  rightTitle: string;
  rightSubtitle: string;
  thirdTitle?: string;
  thirdSubtitle?: string;
  leftBody: string;
  variant: "capture" | "dictation";
  dictationDemo?: DictationDemoData;
}

export interface DictationLoopTimings {
  emptyMs: number;
  placeholdersMs: number;
  partialMs: number;
  fullMs: number;
  resetMs: number;
  leftDelayMs: number;
}

export interface DictationDemoData {
  leftTypedText: string;
  rightParagraphs: string[];
  timings: DictationLoopTimings;
}

export interface WhySlidePointData {
  title: string;
  body: string;
  iconSrc: string;
  iconAlt: string;
}

export interface WhySlideQuoteData {
  text: string;
}

export interface UsedByCardData {
  role: string;
  outcome: string;
  detail: string;
  tags: string[];
  icon: "doctor" | "secretary" | "guideline";
}

export type AppStripItem =
  | {
      type: "image";
      alt: string;
      src: string;
      boxed?: boolean;
      compact?: boolean;
    }
  | {
      type: "monogram";
      alt: string;
      label: string;
      background: string;
      color: string;
    };

export interface PrivacyCardData {
  title: string;
  body: string;
  iconSrc: string;
  iconAlt: string;
}

export interface WhySlideData {
  eyebrow: string;
  title: string;
  subtitle: string;
  points: WhySlidePointData[];
  quotes: WhySlideQuoteData[];
  footer: string;
}

export interface StartOfferingData {
  title: string;
  body: string;
  actionLabel: string;
  actionHref: string;
  actionType: "internal" | "email";
}

export interface PricingPlanData {
  name: string;
  audience: string;
  monthlyPrice: number | null;
  featuresIntro: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  note?: string;
  highlighted?: boolean;
  badge?: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FooterLinkData {
  label: string;
  href: string;
  type: "route" | "anchor" | "email";
}

export interface FooterGroupData {
  title: string;
  links: FooterLinkData[];
}

export const LANDING_NAV_ITEMS: LandingNavItem[] = [
  { label: "Product", href: "#features" },
  { label: "Privacy", href: "#privacy" },
  { label: "Prijzen", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const HERO_CONTENT = {
  title: "Minder typen. Meer zorg.",
  body: "Werkt op de achtergrond in élke app, in jouw stijl, geoptimaliseerd voor medisch Nederlands.",
  primaryCta: "Download en test meteen",
  primaryCtaAuthed: "Download Carmen",
};

export const COMPARISON_SECTIONS: ComparisonSectionData[] = [
  {
    id: "#features",
    title: "",
    leftTitle: "Typen",
    leftSubtitle: "zonder Carmen",
    rightTitle: "Dictatie",
    rightSubtitle: "met Carmen",
    thirdTitle: "Gestructureerde notities",
    thirdSubtitle: "SOEP met Carmen",
    leftBody:
      "78-jarige patiënte met progressieve dyspneu en palpitaties. ECG bevestigt VKF met snelle respons. Echo en labo aangevraagd.",
    dictationDemo: {
      leftTypedText:
        "78-jarige patiënte met progressieve dyspneu en palpitaties. ECG bevestigt VKF met snelle respons. Echo en labo aangevraagd.",
      rightParagraphs: [
        "78-jarige patiënte op consult wegens twee weken progressieve inspanningsdyspneu en recidiverende palpitaties. Geen thoracale pijn of syncope, wel duidelijke daling van de inspanningstolerantie en enkeloedeem tegen de avond.",
        "Bij onderzoek onregelmatige tachycardie rond 128 per minuut, bloeddruk 138 over 84 en saturatie 97 procent in kamerlucht. ECG bevestigt voorkamerfibrillatie met snelle ventriculaire respons. Labo, NT-proBNP en transthoracale echocardiografie aangevraagd ter evaluatie van structureel hartlijden en volume-status.",
        "Plan: opstart rate control met bisoprolol, anticoagulatiebeleid beoordelen op basis van CHA2DS2-VASc en HAS-BLED, huisartsbrief vandaag nog uitsturen. Secretariaat plant echocardiografie en controleconsult binnen zes weken.",
      ],
      timings: {
        emptyMs: 1700,
        placeholdersMs: 4400,
        partialMs: 5000,
        fullMs: 7000,
        resetMs: 3000,
        leftDelayMs: 1200,
      },
    },
    variant: "dictation",
  },
];

export const TIME_SAVED = {
  value: 72,
  label: "minuten administratietijd bespaard per dag",
};

export const USED_BY_CARDS: UsedByCardData[] = [
  {
    role: "Artsen",
    outcome: "Werk consultnotities af voor de patiënt vertrekt.",
    detail:
      "Leg de consultatie één keer vast en ga meteen verder naar verslag, brief en vervolgplan.",
    tags: ["Consultbrief", "EMR klaar"],
    icon: "doctor",
  },
  {
    role: "Medewerkers",
    outcome: "Maak brieven, verslagen en notities sneller klaar.",
    detail:
      "Werk met voorbereide tekst en overdracht, terwijl de arts de medische inhoud blijft controleren.",
    tags: ["Brief klaar", "Overdracht"],
    icon: "secretary",
  },
  {
    role: "Cardiologie",
    outcome: "Laat richtlijnsignalen meelopen met het verslag.",
    detail:
      "Maak ESC-signalen zichtbaar naast de documentatie, zonder tussenstappen of losse checklists.",
    tags: ["ESC", "Checklist"],
    icon: "guideline",
  },
];

export const WHY_SLIDE: WhySlideData = {
  eyebrow: "Carmen Intro & Visie",
  title: "Waarom zorgteams Carmen gebruiken",
  subtitle: "",
  points: [
    {
      title: "Minder corrigeren",
      body:
        "Carmen begrijpt medische terminologie, afkortingen en verslagtaal, zodat je minder tijd verliest aan nabewerking.",
      iconSrc: "/marketing-icons/Afbeelding1.png",
      iconAlt: "Domeinkennis icoon",
    },
    {
      title: "Blijf in je bestaande workflow",
      body:
        "Gebruik Carmen in je EPD, Word, mail of browser zonder nieuwe interface of integratiecode.",
      iconSrc: "/marketing-icons/Afbeelding20.png",
      iconAlt: "Geen nieuwe interface nodig icoon",
    },
    {
      title: "Tekst zoals jij werkt",
      body:
        "Vat dictaten en gesprekken samen in je eigen stijl en gebruik snelteksten voor terugkerende formuleringen.",
      iconSrc: "/marketing-icons/Afbeelding6.png",
      iconAlt: "Intelligente verwerking icoon",
    },
  ],
  quotes: [
    {
      text: "Verslag klaar wanneer het consult eindigt.",
    },
    {
      text: "Meer aandacht voor de patiënt, minder voor het scherm.",
    },
    {
      text: "Geen nieuwe werkwijze of extra scherm nodig.",
    },
    {
      text: "Verslagen in je eigen medische stijl.",
    },
    {
      text: "Brieven en verslagen sneller klaar voor verzending.",
    },
    {
      text: "Consultnotities zonder het gesprek te onderbreken.",
    },
    {
      text: "Dictaten meteen omgezet naar bruikbare medische tekst.",
    },
    {
      text: "Een Bulgaarse patiënt begrijpen zonder Nederlands, Frans of Engels.",
    },
    {
      text: "Uitstekend medisch Nederlands in vergelijking met andere apps.",
    },
  ],
  footer: "Copyright @Carmen 2026",
};

export const APP_STRIP_ITEMS: AppStripItem[] = [
  { type: "image", alt: "Chrome", src: "/landing-app-icons/chrome.png" },
  { type: "image", alt: "Mail", src: "/landing-app-icons/mail.png" },
  { type: "image", alt: "Outlook", src: "/landing-app-icons/outlook.png?v=3" },
  { type: "image", alt: "CGM", src: "/landing-app-icons/cgm-logo.png", compact: true },
  { type: "image", alt: "CareConnect (Corilus)", src: "/landing-app-icons/careconnect.png?v=2", compact: true },
  { type: "image", alt: "HealthOne", src: "/landing-app-icons/healthone.png", compact: true },
  { type: "image", alt: "Medispring", src: "/landing-app-icons/medispring.svg?v=2", compact: true },
  { type: "image", alt: "Word", src: "/landing-app-icons/word.png" },
  { type: "image", alt: "Citrix", src: "/landing-app-icons/citrix.png", compact: true },
  { type: "image", alt: "HiX", src: "/hix.png?v=2", compact: true },
  { type: "image", alt: "Carmen", src: "/carmen-icon-inverted.png", boxed: true },
  { type: "image", alt: "Notities", src: "/landing-app-icons/notes.png" },
  { type: "image", alt: "Epic", src: "/landing-app-icons/epic-logo.png" },
  { type: "image", alt: "Zorgi", src: "/landing-app-icons/zorgi.png", compact: true },
];

export const PRIVACY_CARDS: PrivacyCardData[] = [
  {
    title: "Minimale opslag van medische data.",
    body: "Audio en transcript worden verwerkt om de tekst te maken en opslag wordt beperkt tot wat nodig is voor de workflow.",
    iconSrc: "/marketing-icons/Afbeelding16.png",
    iconAlt: "Lokale notitie-opslag icoon",
  },
  {
    title: "Medische gegevens verwerkt via Europese servers.",
    body: "De verwerking van medische audio en tekst gebeurt via gecontroleerde Europese infrastructuur.",
    iconSrc: "/marketing-icons/afbeelding17.png",
    iconAlt: "Europese serververwerking icoon",
  },
  {
    title: "AI-verwerking via SOC 2-gecertificeerde verwerkers.",
    body: "De AI-verwerking gebeurt via SOC 2-gecertificeerde verwerkers en de data wordt daarbij niet bewaard na de verwerking. Opslag volgt daarna de bewaartermijnen van je organisatie.",
    iconSrc: "/marketing-icons/afbeelding18.png",
    iconAlt: "Third-party verwerker icoon",
  },
];

export const START_OFFERINGS: StartOfferingData[] = [
  {
    title: "AI Spraak-naar-tekst",
    body: "Test de applicatie gratis in je medische workflow.",
    actionLabel: "Inloggen",
    actionHref: "/login",
    actionType: "internal",
  },
  {
    title: "Workflow-AI-integratie",
    body: "Breder samenwerken voor integratie van AI in de medische workflow.",
    actionLabel: "Contact",
    actionHref: "mailto:info@askcarmen.eu",
    actionType: "email",
  },
];

export const PRICING_PLANS: PricingPlanData[] = [
  {
    name: "Premium",
    audience: "Voor dagelijks gebruik*",
    monthlyPrice: 39,
    note: "* 600 audiominuten per maand.",
    featuresIntro: "Inbegrepen:",
    features: [
      "Drie modi: dictaat, sjabloon en vertaling",
      "Persoonlijke sjablonen en sneltoetsen",
      "Eigen woordenboeken en medische terminologie",
      "Gebruik op desktop en mobiel",
      "Beveiligde Europese cloudverwerking met Zero Data Retention en versleutelde opslag op Europese servers",
    ],
    ctaLabel: "Download en test meteen",
    ctaHref: "/signup",
  },
  {
    name: "Unlimited",
    audience: "Voor onbeperkt gebruik",
    monthlyPrice: 149,
    featuresIntro: "Alles van Premium, plus:",
    features: [
      "Onbeperkte audiominuten per gebruiker",
      "Prioritaire ondersteuning",
      "Persoonlijke optimalisatiesessie",
      "Afstemming van verslagstijl, sjablonen en terminologie",
    ],
    ctaLabel: "Download en test meteen",
    ctaHref: "/signup",
    highlighted: true,
    badge: "Aanbevolen",
  },
  {
    name: "Enterprise",
    audience: "Voor praktijken en organisaties",
    monthlyPrice: null,
    featuresIntro: "Alles van Unlimited, plus:",
    features: [
      "Centraal admin-dashboard voor gebruikers en afdelingen",
      "Installatie en configuratie op maat",
      "Onboarding en training voor het team",
      "Ondersteuning en floorwalking bij de livegang",
      "Doorlopend onderhoud, beveiliging en functionele updates",
      "Privacy- en beveiligingsdocumentatie",
      "Pilot en gefaseerde uitrol op maat",
    ],
    ctaLabel: "Contact",
    ctaHref: "/contact",
  },
];

export const FAQS: FaqItem[] = [
  {
    question: "Hoe start ik met Carmen in mijn praktijk of dienst?",
    answer:
      "U registreert zich via de website. Daarna nemen we contact met u op en bezorgen we de Carmen-applicatie samen met korte installatie-instructies. Na de installatie logt u in met uw account en kunt u meteen starten. Wilt u Carmen met een volledige praktijk of dienst gebruiken, dan helpen we u graag met de onboarding via info@askcarmen.eu.",
  },
  {
    question: "Hoe werkt Carmen tijdens het consult?",
    answer:
      "Kies de modus Sjabloon wanneer Carmen tijdens het consult mag meeluisteren. U voert het gesprek zoals gewoonlijk; na afloop maakt Carmen er automatisch een gestructureerd medisch verslag van. Bij een taalbarrière kunt u de aparte modus Vertaal gebruiken om het gesprek live tussen Nederlands en de taal van uw patiënt te vertalen.",
  },
  {
    question: "Hoe werkt Carmen na het consult?",
    answer:
      "Kies de modus Dictaat wanneer u na het consult zelf uw bevindingen inspreekt. Plaats uw cursor in het EPD, een brief, e-mail of een andere applicatie en druk op de sneltoets die u zelf in Carmen hebt ingesteld. Met dezelfde sneltoets stopt u de opname. Carmen behoudt wat u dicteert, werkt de tekst medisch en taalkundig af en plakt het resultaat automatisch waar uw cursor staat. Daarvoor is geen technische integratie met uw applicatie nodig. Uw opnames en teksten worden bovendien gesynchroniseerd tussen mobiel en desktop, zodat u ze gemakkelijk terugvindt.",
  },
  {
    question: "Werkt Carmen binnen mijn huidige workflow en applicaties?",
    answer:
      "Ja. U blijft gewoon werken in uw vertrouwde EPD, brief, e-mail of andere applicatie. Carmen plakt de afgewerkte tekst waar uw cursor staat, zodat u geen nieuw patiëntendossier of aparte werkwijze hoeft aan te leren.",
  },
  {
    question: "Welke talen ondersteunt de vertaalmodus?",
    answer:
      "De vertaalmodus vertaalt tussen Nederlands en 56 patiënttalen. U kunt de taal automatisch laten herkennen of zelf kiezen uit: Afrikaans, Arabisch, Armeens, Azerbeidzjaans, Bosnisch, Bulgaars, Catalaans, Chinees, Deens, Duits, Engels, Estisch, Fins, Frans, Galicisch, Grieks, Hebreeuws, Hindi, Hongaars, IJslands, Indonesisch, Italiaans, Japans, Kannada, Kazachs, Koreaans, Kroatisch, Lets, Litouws, Macedonisch, Maleis, Maori, Marathi, Nepalees, Noors, Oekraïens, Perzisch, Pools, Portugees, Roemeens, Russisch, Servisch, Slowaaks, Sloveens, Spaans, Swahili, Tagalog, Tamil, Thais, Tsjechisch, Turks, Urdu, Vietnamees, Welsh, Wit-Russisch en Zweeds.",
  },
  {
    question: "Wat gebeurt er met medische data en transcripties?",
    answer:
      "AI-verwerking gebeurt via Europese infrastructuur onder Zero Data Retention-afspraken: de AI-provider bewaart klantinhoud niet na de verwerking. Gegevens die daarna binnen Carmen worden bewaard, worden versleuteld opgeslagen. Uw gegevens worden niet gebruikt om AI-modellen te trainen. Meer details vindt u in het privacybeleid en het Trust Center.",
  },
  {
    question: "Kan ik support spreken of een pilot bespreken?",
    answer:
      "Ja. Voor bredere workflow-integratie, documentatievragen of onboarding kunt u rechtstreeks mailen naar info@askcarmen.eu.",
  },
];

export const FOOTER_GROUPS: FooterGroupData[] = [
  {
    title: "Product",
    links: [
      { label: "Product", href: "#features", type: "anchor" },
      { label: "Privacy by design", href: "#privacy", type: "anchor" },
      { label: "Prijzen", href: "#pricing", type: "anchor" },
      { label: "FAQ", href: "#faq", type: "anchor" },
      { label: "Inloggen", href: "/login", type: "route" },
    ],
  },
  {
    title: "Juridisch",
    links: [
      { label: "Privacy", href: "/privacy", type: "route" },
      { label: "Trust Center", href: "/trust", type: "route" },
      { label: "Sub-processors", href: "/sub-processors", type: "route" },
      { label: "Gebruiksvoorwaarden", href: "/terms", type: "route" },
    ],
  },
  {
    title: "Bedrijf",
    links: [
      { label: "Over Carmen", href: "/about", type: "route" },
      { label: "Contact", href: "/contact", type: "route" },
    ],
  },
];
